import type { RefObject } from 'react';
import { FloatingMenu } from '../../../FloatingMenu';
import type { CommunicationPatientRef } from '../../../../services/communications';
import { ALREADY_NOTED_LABEL } from './AddPatientNotePatientsField';

interface RestrictedPatientPickerProps {
    triggerRef: RefObject<HTMLElement>;
    options: CommunicationPatientRef[];
    alreadyNotedIds: Set<string>;
    onPick: (patient: CommunicationPatientRef) => void;
    onClose: () => void;
}

/** The picker for a practice-less thread: no practice to search, so it can
 *  only offer patients already linked to the thread but not yet selected -
 *  minus the ones already noted from this entry, which still show, disabled. */
export function RestrictedPatientPicker({
    triggerRef,
    options,
    alreadyNotedIds,
    onPick,
    onClose
}: RestrictedPatientPickerProps) {
    return (
        // No matchTriggerWidth - see PatientPicker.tsx's identical note; a
        // fixed width keeps this readable regardless of the trigger's size.
        <FloatingMenu open triggerRef={triggerRef} onClose={onClose}>
            <div
                className="bg-white border border-gray-200 rounded-xl shadow-lg p-1.5 w-[320px] max-h-[220px] overflow-y-auto overflow-x-hidden"
                onMouseDown={(e) => e.stopPropagation()}
            >
                {options.map((patient) => {
                    const noted = alreadyNotedIds.has(patient.id);
                    return (
                        <button
                            key={patient.id}
                            type="button"
                            disabled={noted}
                            title={noted ? ALREADY_NOTED_LABEL : undefined}
                            onClick={() => onPick(patient)}
                            className={`w-full flex items-center gap-2.5 text-left px-3 py-2 rounded-lg text-[13px] font-semibold ${
                                noted
                                    ? 'opacity-40 cursor-not-allowed text-gray-500'
                                    : 'text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            <span title={patient.name} className="flex-1 min-w-0 truncate">
                                {patient.name}
                            </span>
                            {noted && (
                                <span className="ml-auto text-[11px] font-normal text-gray-400 shrink-0">
                                    {ALREADY_NOTED_LABEL}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </FloatingMenu>
    );
}
